import { Fragment, FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { nanoid } from 'nanoid/non-secure';
import { Box, SxStyleProp } from 'theme-ui';
import { ScalingType, Scene, SceneType } from 'types/Scene';
import { ActiveTool, ToolName } from 'types/AppState';
import {
  createScene,
  getCurrentScene,
  getSceneIdsWithPaidFeatures,
  getScenes,
  selectScene,
  setSceneTransition,
  trimScene,
  updateScene
} from 'modules/scenes';
import { deleteScene } from 'modules/effects';
import SceneCard, { SceneCardProps } from './scenesPanel/SceneCard';
import AddScene, { AddSceneProps } from './scenesPanel/AddScene';
import TransitionCard, { TransitionCardProps } from './scenesPanel/TransitionCard';
import { getCurrentTransitionId, selectCurrentTransition } from 'modules/transitions';
import { getActiveTool, setActiveTool } from 'modules/activeTool';
import Scroll from 'shared/Scroll';
import { CloseTag } from 'shared/CloseTag';
import { getSceneVolume, updateSceneVolume } from 'modules/sceneVolume';
import { SceneVolume } from 'types/sceneVolume';
import { getCreationProgressOfScene } from 'modules/scenesCreationProgress';
import { DURATION_REQUIRED_FOR_TRANSITION } from 'constants/scenes';
import { isNil } from 'ramda';
import {
  getDownloadingProgressOfScene,
  setScenesDownloadingProgress,
  SetScenesDownloadingProgressAction
} from 'modules/sceneDownloadingProgress';
import analytics from 'utils/analytics';
import { HelpWidget } from './HelpWidget';
import { isSidebarOpen, openSidebar, toggleSidebar } from 'modules/sidebar';
import { Sidebar } from 'types/Sidebar';
import { Workspace } from 'types/Clipboard';
import { setCurrentTextId } from 'modules/currentTextId';
import { setCurrentWorkspace } from 'modules/workspace';
import { getSceneSources, ScenesLoader } from 'modules/scenesLoader';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { DragDropSceneWrapper } from './scenesPanel/DragDropSceneWrapper';
import { TransitionType } from 'types/Transitions';
import { contentIndependentTools } from 'constants/tools';
import { saveRevision } from 'modules/revisions';
import { transitionNames } from 'utils/transitionMessages';
import { useTranslator } from '@picsart/rc/services/localization';
import { usePlayer } from 'utils/PlayerContext';

export type ScenesPanelProps = {
  scenes: Scene[];
  currentSceneId: string;
  selectScene: (id: string) => SceneCardProps['selectHandler'];
  removeScene: (id: string) => SceneCardProps['removeHandler'];
  addScene: AddSceneProps['addHandler'];
  trimContent: (id: string) => SceneCardProps['trimHandler'];
  currentTransitionId?: string;
  selectTransition: (id: string) => TransitionCardProps['selectHandler'];
  scalingModeChangeHandler: (item: Scene) => SceneCardProps['scaleModeChangeHandler'];
  setMute: (id: string) => (isMute: boolean) => void;
  sceneVolume: SceneVolume;
  setScenesDownloadingProgressHandler: SetScenesDownloadingProgressAction;
  handleRemoveTransition: (id: string) => () => void;
  paidScenes: string[];
  isOpen: boolean;
  handleToggleSidebar: () => void;
  handleSetWorkspace: (workspace: Workspace) => void;
  setPlayingHandler: SceneCardProps['setPlayingHandler'];
  sources: ScenesLoader['sources'];
};

const sceneCardSelectWrapperStyle: SxStyleProp = {
  position: 'relative',
  ':hover .selection-border': {
    opacity: 1
  },
  ':hover .sceneCard-wrap': {
    borderRadius: '10px'
  }
};

const sceneCardSelectWrapperSelectedStyle: SxStyleProp = {
  position: 'relative',
  '& .sceneCard-wrap': {
    borderRadius: '10px'
  }
};

const selectionBorderSelectedStyle: SxStyleProp = {
  borderColor: '#282B36',
  opacity: 1,
  '.sceneCard-wrap': {
    borderRadius: '10px'
  }
};

const selectionBorderInnerStyle: SxStyleProp = {
  position: 'relative',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  border: '2px solid',
  borderColor: 'white',
  borderRadius: '6px'
};

const selectionBorderStyle: SxStyleProp = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 'selectionBorder',
  pointerEvents: 'none',
  opacity: 0,
  borderRadius: '8px',
  border: '2px solid',
  borderColor: 'grey3'
};

const ScenesPanelBase: FC<ScenesPanelProps> = ({
  scenes,
  currentSceneId,
  removeScene,
  trimContent,
  selectScene,
  addScene,
  currentTransitionId,
  selectTransition,
  scalingModeChangeHandler,
  handleRemoveTransition,
  sceneVolume,
  setMute,
  setScenesDownloadingProgressHandler,
  paidScenes,
  isOpen,
  handleToggleSidebar,
  handleSetWorkspace,
  setPlayingHandler,
  sources
}) => {
  const translator = useTranslator();

  return (
    <Fragment>
      <Box
        onClick={() => handleSetWorkspace(Workspace.ScenesPanel)}
        sx={{
          width: '218px',
          height: '100%',
          bg: 'background',
          flex: '0 0 218px',
          position: 'relative',
          zIndex: 10,
          borderRadius: '8px',
          marginLeft: '8px',
          marginRight: isOpen ? 0 : '-232px'
        }}
      >
        <div sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <Scroll>
            <Box sx={{ px: '10px', py: '8px' }}>
              <DragDropSceneWrapper scenes={scenes} currentSceneId={currentSceneId}>
                {dropSceneHandler => (
                  <DragDropContext onDragEnd={dropSceneHandler}>
                    <Droppable droppableId="droppable">
                      {droppable => (
                        <div {...droppable.droppableProps} ref={droppable.innerRef}>
                          {scenes.map((item, idx, { length }) => (
                            <Draggable key={item.id} draggableId={item.id} index={idx}>
                              {draggable => (
                                <Box
                                  ref={draggable.innerRef}
                                  {...draggable.draggableProps}
                                  {...draggable.dragHandleProps}
                                  mb={3}
                                >
                                  <Box
                                    sx={
                                      item.id === currentSceneId
                                        ? sceneCardSelectWrapperSelectedStyle
                                        : sceneCardSelectWrapperStyle
                                    }
                                  >
                                    <Box
                                      className="selection-border"
                                      sx={
                                        item.id === currentSceneId
                                          ? {
                                              ...selectionBorderStyle,
                                              ...selectionBorderSelectedStyle
                                            }
                                          : selectionBorderStyle
                                      }
                                    >
                                      <Box sx={selectionBorderInnerStyle} />
                                    </Box>
                                    <SceneCardComputeProgress
                                      {...item}
                                      localSource={sources[item.id]}
                                      isScenePaid={paidScenes.includes(item.id)}
                                      setScenesDownloadingProgressHandler={
                                        setScenesDownloadingProgressHandler
                                      }
                                      mute={!sceneVolume[item.id]?.volume}
                                      selectHandler={selectScene(item.id)}
                                      removeHandler={
                                        !(idx === 0 && item.type === SceneType.EMPTY) &&
                                        removeScene(item.id)
                                      }
                                      trimHandler={trimContent(item.id)}
                                      active={item.id === currentSceneId}
                                      scaleModeChangeHandler={scalingModeChangeHandler(item)}
                                      setMute={setMute(item.id)}
                                      setPlayingHandler={setPlayingHandler}
                                      isUploaded={item.uploaded}
                                    />
                                  </Box>
                                  {idx < length - 1 && (
                                    <TransitionCard
                                      name={
                                        item.transition.type === TransitionType.Shape
                                          ? translator(item.transition.name)
                                          : translator(transitionNames[item.transition.type])
                                      }
                                      type={item.transition.type}
                                      selected={item.id === currentTransitionId}
                                      selectHandler={selectTransition(item.id)}
                                      handleRemoveTransition={handleRemoveTransition(item.id)}
                                      isDurationInvalid={
                                        item.duration < DURATION_REQUIRED_FOR_TRANSITION
                                      }
                                      isNextSceneEmpty={scenes[idx + 1]?.type === SceneType.EMPTY}
                                    />
                                  )}
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {droppable.placeholder}
                          <AddScene addHandler={addScene} />
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </DragDropSceneWrapper>
            </Box>
          </Scroll>
        </div>
        <CloseTag
          data-test="close"
          isOpen={isOpen}
          sx={{
            right: 231,
            bottom: 300,
            transform: 'rotate(180deg)',
            zIndex: 'closeTag',
            marginRight: '-13px'
          }}
          handleClick={handleToggleSidebar}
        />
      </Box>
      <HelpWidget isScenesPanelOpened={isOpen} />
    </Fragment>
  );
};

const SceneCardComputeProgress: FC<
  Omit<SceneCardProps, 'creationProgress' | 'downloadingProgress'>
> = props => {
  const creationProgress: SceneCardProps['creationProgress'] = useSelector(
    getCreationProgressOfScene(props)
  );
  const downloadingProgress: SceneCardProps['downloadingProgress'] = useSelector(
    getDownloadingProgressOfScene(props.id)
  );
  const sceneCardProps: SceneCardProps = { ...props, creationProgress, downloadingProgress };
  return <SceneCard {...sceneCardProps} />;
};

const ScenePanel = () => {
  const scenes = useSelector(getScenes);
  const currentScene = useSelector(getCurrentScene);
  const currentTransitionId = useSelector(getCurrentTransitionId);
  const dispatch = useDispatch();
  const paidScenes = useSelector(getSceneIdsWithPaidFeatures);
  const isOpen = useSelector(isSidebarOpen(Sidebar.ScenesPanel));
  const sceneSources = useSelector(getSceneSources);
  const activeTool = useSelector(getActiveTool);
  const player = usePlayer();

  const handleSetWorkspace = useCallback((workspace: Workspace) => {
    dispatch(setCurrentWorkspace(workspace));
  }, []);

  const selectSceneHandler: ScenesPanelProps['selectScene'] = id => () => {
    dispatch(selectScene(id));
  };
  const removeScene: ScenesPanelProps['removeScene'] = id => () => {
    dispatch(deleteScene(id));
  };
  const addScene: ScenesPanelProps['addScene'] = () => {
    dispatch(createScene(nanoid(), true));
  };
  const selectTransition: ScenesPanelProps['selectTransition'] = id => () => {
    analytics.videoEditItemOpen(ToolName.Transition);
    player.pause();
    dispatch(selectCurrentTransition(id));
    dispatch(openSidebar(Sidebar.ToolPanel));
    dispatch(setActiveTool(ActiveTool.Transition));
  };
  const trimContent: ScenesPanelProps['trimContent'] = id => (duration, trimStart) => {
    const scene = scenes.find(s => s.id === id);
    const lastEnd = scene.duration + scene.trimStart;
    const end = duration + trimStart;

    analytics.trimAction({
      left_moved: scene.trimStart !== trimStart,
      right_moved: lastEnd !== end,
      media_type: 'video'
    });
    dispatch(trimScene(id, duration, trimStart));
  };

  const isToolForced = useRef<boolean>(false);

  useEffect(() => {
    const params: URLSearchParams = new URLSearchParams(window.location.search);
    const tool: string | null = params.get('tool');
    if (!isNil(tool)) {
      isToolForced.current = true;
    }
  }, [isToolForced]);

  useEffect(() => {
    if (isToolForced.current) {
      isToolForced.current = false;
      return;
    }

    if (currentScene?.type === SceneType.EMPTY && !contentIndependentTools.includes(activeTool)) {
      dispatch(setActiveTool(ActiveTool.PhotoSidebar));
    }
  }, [currentScene?.type, isToolForced, activeTool]);

  const scalingModeChangeHandler = useCallback(
    (scene: Scene) => (scalingMode: ScalingType) => {
      dispatch(updateScene({ scalingMode }, scene.id));
      analytics.sceneFitAction(scalingMode);
      dispatch(saveRevision(ToolName.Scene));
    },
    []
  );

  const setMute = (id: string) => (isMute: boolean) => {
    dispatch(updateSceneVolume(id, isMute ? 0 : 0.5));
    analytics.muteAction(isMute ? 'unmute' : 'mute');
    dispatch(saveRevision(ToolName.Scene));
  };

  const sceneVolume = useSelector(getSceneVolume);

  const setScenesDownloadingProgressHandler = (progress: number, sceneId: string) => {
    dispatch(setScenesDownloadingProgress(progress, sceneId));
  };

  const handleRemoveTransition = (id: string) => () => {
    analytics.transitionAction({ action: 'delete' });
    dispatch(setSceneTransition(null, id));
  };

  const handleToggleSidebar = useCallback(() => {
    dispatch(toggleSidebar(Sidebar.ScenesPanel));
  }, []);

  useEffect(() => {
    dispatch(setCurrentTextId([]));
  }, []);

  const setPlayingHandler: ScenesPanelProps['setPlayingHandler'] = useCallback(
    isPlaying => {
      if (isPlaying) {
        player.pause();
      } else {
        player.play();
      }
    },
    [player]
  );

  return (
    currentScene && (
      <ScenesPanelBase
        handleToggleSidebar={handleToggleSidebar}
        handleSetWorkspace={handleSetWorkspace}
        handleRemoveTransition={handleRemoveTransition}
        paidScenes={paidScenes}
        setScenesDownloadingProgressHandler={setScenesDownloadingProgressHandler}
        setMute={setMute}
        sceneVolume={sceneVolume}
        scenes={scenes}
        currentSceneId={currentScene.id}
        trimContent={trimContent}
        selectScene={selectSceneHandler}
        removeScene={removeScene}
        addScene={addScene}
        currentTransitionId={currentTransitionId}
        selectTransition={selectTransition}
        scalingModeChangeHandler={scalingModeChangeHandler}
        isOpen={isOpen}
        setPlayingHandler={setPlayingHandler}
        sources={sceneSources}
      />
    )
  );
};

ScenePanel.dispayName = 'ScenePanel';

export default ScenePanel;
